import { normalizePostAttachments, type Post } from "@/entities/post/types";
import type {
  Catalog,
  ThreadChunkElement,
  ThreadState,
  ThreadSummary,
  ThreadView,
} from "@/entities/thread/types";
import { isThreadChunkPost } from "@/entities/thread/types";

/** Normalize a thread summary received from catalog-like endpoints. */
export function normalizeThreadSummaryAttachments(
  thread: ThreadSummary,
): ThreadSummary {
  return {
    ...thread,
    opPost: normalizePostAttachments(thread.opPost),
  };
}

/** Normalize every post in a full thread response as one atomic response. */
export function normalizeThreadViewAttachments(thread: ThreadView): ThreadView {
  return {
    ...thread,
    posts: thread.posts.map((post) => normalizePostAttachments(post)),
  };
}

/** Normalize ordered attachments for a catalog response. */
export function normalizeCatalogAttachments(catalog: Catalog): Catalog {
  return {
    ...catalog,
    threads: catalog.threads.map(normalizeThreadSummaryAttachments),
  };
}

/** Normalize public chunk posts while preserving unavailable markers. */
export function normalizeThreadChunkAttachments(
  elements: ThreadChunkElement[],
): ThreadChunkElement[] {
  return elements.map((element) =>
    isThreadChunkPost(element) ? normalizePostAttachments(element) : element,
  );
}

/** Normalize state.newPosts only after the whole state response is available. */
export function normalizeThreadStateAttachments(
  state: ThreadState,
): ThreadState {
  return {
    ...state,
    newPosts: state.newPosts
      ? normalizeThreadChunkAttachments(state.newPosts)
      : undefined,
  };
}

/** Normalize search results at their API boundary. */
export function normalizeSearchPostAttachments(posts: Post[]): Post[] {
  return posts.map((post) => normalizePostAttachments(post));
}
